/**
 * ResearchScopeSharingModal Component
 * 
 * Modal for managing sharing settings of a Research Scope.
 * Uses EntraUserPicker to search and select Entra users/groups.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  PrimaryButton,
  DefaultButton,
  Stack,
  Toggle,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Label,
  Separator
} from '@fluentui/react';
import {
  PersonAddRegular,
  PeopleRegular,
  GlobeRegular,
  LockClosedRegular
} from '@fluentui/react-icons';
import { EntraUserPicker } from '../EntraUserPicker';
import { 
  updateResearchScope, 
  ResearchScopeDetail 
} from '../../api/researchScopeApi';

interface ResearchScopeSharingModalProps {
  scope: ResearchScopeDetail;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (scope: ResearchScopeDetail) => void;
}

export const ResearchScopeSharingModal: React.FC<ResearchScopeSharingModalProps> = ({
  scope,
  isOpen,
  onClose,
  onUpdated
}) => {
  const [sharedWithUsers, setSharedWithUsers] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize from scope
  useEffect(() => {
    if (scope) {
      setSharedWithUsers(scope.shared_with_users || []);
      setIsPublic(scope.is_public || false);
    }
  }, [scope]);

  // Reset success message when modal opens
  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedScope = await updateResearchScope(scope.id, {
        shared_with_users: sharedWithUsers,
        is_public: isPublic
      });
      
      setSuccess(true);
      onUpdated(updatedScope);
      
      // Close after a brief delay to show success
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleClose}
      isBlocking={isSaving}
      styles={{
        main: {
          maxWidth: '500px',
          minWidth: '400px'
        }
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px',
        borderBottom: '1px solid #e1e1e1',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <PersonAddRegular style={{ fontSize: '24px', color: '#2196f3' }} />
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            Share Research Scope
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
            {scope?.name}
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        {error && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setError(null)}
            styles={{ root: { marginBottom: '16px' } }}
          >
            {error}
          </MessageBar>
        )}

        {success && (
          <MessageBar
            messageBarType={MessageBarType.success}
            styles={{ root: { marginBottom: '16px' } }}
          >
            Sharing settings updated successfully!
          </MessageBar>
        )}

        <Stack tokens={{ childrenGap: 20 }}>
          {/* Public toggle */}
          <div style={{
            padding: '16px',
            backgroundColor: isPublic ? '#e3f2fd' : '#fafafa',
            borderRadius: '8px',
            border: isPublic ? '1px solid #2196f3' : '1px solid #e1e1e1',
            transition: 'all 0.2s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ 
                marginTop: '2px',
                color: isPublic ? '#2196f3' : '#666'
              }}>
                {isPublic ? (
                  <GlobeRegular style={{ fontSize: '20px' }} />
                ) : (
                  <LockClosedRegular style={{ fontSize: '20px' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Toggle
                  label="Make this scope public"
                  checked={isPublic}
                  onChange={(_, checked) => setIsPublic(!!checked)}
                  disabled={isSaving}
                  styles={{ root: { marginBottom: '4px' } }}
                />
                <p style={{ 
                  margin: 0, 
                  fontSize: '12px', 
                  color: '#666',
                  lineHeight: 1.4
                }}>
                  {isPublic 
                    ? 'Anyone in your organization can view and query this scope.'
                    : 'Only you and people you share with can access this scope.'
                  }
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* User picker */}
          <div>
            <Label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              marginBottom: '8px'
            }}>
              <PeopleRegular style={{ fontSize: '16px' }} />
              Share with specific people
            </Label>
            
            <EntraUserPicker
              label=""
              placeholder="Search for users by name or email..."
              selectedEmails={sharedWithUsers}
              onSelectionChange={setSharedWithUsers}
              disabled={isSaving}
              maxSelection={50}
              description="Selected users will be able to view and query this research scope."
            />
          </div>

          {/* Info box */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#856404',
            lineHeight: 1.5
          }}>
            <strong>Note:</strong> Shared users can view and query documents, but cannot add, 
            delete, or modify the scope settings. Only the owner can manage the scope.
          </div>
        </Stack>
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e1e1e1',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px'
      }}>
        <DefaultButton
          text="Cancel"
          onClick={handleClose}
          disabled={isSaving}
        />
        <PrimaryButton
          text={isSaving ? 'Saving...' : 'Save Changes'}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving && <Spinner size={SpinnerSize.xSmall} style={{ marginRight: '8px' }} />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default ResearchScopeSharingModal;
